import { IconButtonVariant } from '@/components/Redactie/IconButton/IconButton';
import UploadButton from '@/components/Redactie/UploadButton/UploadButton';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import EditButtonBar from '@/components/Redactie/IconButton/EditButtonBar';
import Modal from './Modal';
import P from '@/components/Redactie/P/P';
import Icon from '@mdi/react';
import { mdiInformation } from '@mdi/js';

interface ImagePickerModalProps {
    title: string;
    description?: string;
    isOpen?: boolean;
    originalImage?: string;
    originalAltText?: string;
    imageWidth?: number;
    imageHeight?: number;
    imageMimeType?: 'image/jpeg' | 'image/png';
    onClose: () => void;
    onImageSelected: (image?: File, altText?: string) => void;
}

const ImagePickerModal = ({
    title,
    description,
    isOpen,
    originalImage,
    originalAltText,
    imageWidth,
    imageHeight,
    imageMimeType = 'image/jpeg',
    onClose,
    onImageSelected,
}: ImagePickerModalProps) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [hasDrawnOriginalImage, setHasDrawnOriginalImage] = useState(false);
    const [hasChangedImage, setHasChangedImage] = useState(false);
    const [altText, setAltText] = useState(originalAltText ?? '');

    // Draw original image to canvas, only once
    useLayoutEffect(() => {
        const canvas = canvasRef.current;
        if (
            !hasDrawnOriginalImage &&
            !hasChangedImage &&
            originalImage &&
            canvas
        ) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                drawImageToCanvas(originalImage, canvas, ctx, false);
                setHasDrawnOriginalImage(true);
            }
        }
    }, [isOpen]);

    useEffect(() => {
        setHasDrawnOriginalImage(false);
        setHasChangedImage(false);
    }, [isOpen, originalImage]);

    useEffect(() => {
        setAltText(originalAltText ?? '');
    }, [originalAltText]);

    const drawFileToCanvas = (file: File, canvas: HTMLCanvasElement) => {
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }
        const reader = new FileReader();
        reader.onload = (event) => {
            if (event.target?.result) {
                // Clear the canvas
                ctx.clearRect(0, 0, canvas.width, canvas.height);

                // Draw the image to the canvas
                drawImageToCanvas(
                    event.target.result as string,
                    canvas,
                    ctx,
                    true,
                );
            }
        };
        reader.readAsDataURL(file);
    };

    const drawImageToCanvas = (
        imageUrl: string,
        canvas: HTMLCanvasElement,
        ctx: CanvasRenderingContext2D,
        isUserAction = false,
    ) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            // Draw the image like the css property object-fit: cover
            // If the image is a different aspect ratio than the canvas, the image will be cropped
            const imageAspectRatio = img.width / img.height;
            const canvasAspectRatio = canvas.width / canvas.height;
            if (imageAspectRatio > canvasAspectRatio) {
                const imageWidth = canvas.height * imageAspectRatio;
                const imageX = (canvas.width - imageWidth) / 2;
                ctx.drawImage(img, imageX, 0, imageWidth, canvas.height);
            }
            if (imageAspectRatio < canvasAspectRatio) {
                const imageHeight = canvas.width / imageAspectRatio;
                const imageY = (canvas.height - imageHeight) / 2;
                ctx.drawImage(img, 0, imageY, canvas.width, imageHeight);
            }
            if (imageAspectRatio === canvasAspectRatio) {
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            }
            if (isUserAction) setHasChangedImage(true);
        };
        img.src = imageUrl;
    };

    return (
        <Modal title={title} isOpen={isOpen} onClose={onClose}>
            {!!description && (
                <p className="flex gap-2 rounded-md bg-secondary p-4 font-bold text-white">
                    <Icon path={mdiInformation} size={1} className="shrink-0" />
                    {description}
                </p>
            )}
            <div className="relative">
                <canvas
                    ref={canvasRef}
                    className="w-full rounded-md border-2 border-slate-300 bg-slate-200"
                    width={imageWidth}
                    height={imageHeight}
                />
                <div className="absolute left-4 bottom-4 px-2 py-1 bg-black/75 text-white text-sm font-semibold rounded-md">
                    {imageWidth}px &#215; {imageHeight}px
                </div>
            </div>
            <UploadButton
                label="Afbeelding kiezen"
                className="self-center"
                variant={
                    !hasChangedImage
                        ? IconButtonVariant.Primary
                        : IconButtonVariant.Regular
                }
                accept={imageMimeType}
                onChange={(files) => {
                    if (files && files.length > 0 && canvasRef.current) {
                        drawFileToCanvas(files[0], canvasRef.current);
                    }
                }}
            >
                Nieuwe afbeelding
            </UploadButton>
            <hr />
            <P
                isEditable
                placeholder="Alternatief: Beschrijf wat en wie er op deze afbeelding staan en wat er gebeurt, zodat mensen zich goed voor kunnen stellen hoe de afbeelding er uit ziet."
                value={altText}
                onChange={setAltText}
            />
            <hr />
            <EditButtonBar
                saveButton={{
                    show: true,
                    enabled:
                        (hasChangedImage && altText.length > 12) ||
                        (altText !== originalAltText && altText.length > 12),
                    buttonText: 'Kiezen',
                    labelText: 'Afbeelding kiezen',
                    onClick: () => {
                        if (canvasRef.current) {
                            // Convert canvas to image file object
                            canvasRef.current.toBlob(
                                async (blob) => {
                                    if (blob) {
                                        const file = new File(
                                            [blob],
                                            imageMimeType,
                                            {
                                                type: imageMimeType,
                                            },
                                        );
                                        onImageSelected(file, altText);
                                        onClose();
                                    }
                                },
                                imageMimeType,
                                0.9,
                            );
                        }
                    },
                }}
                cancelButton={{
                    show: true,
                    onClick: () => {
                        setHasChangedImage(false);
                        onClose();
                    },
                }}
            />
        </Modal>
    );
};

export default ImagePickerModal;
